import { ComponentChildren, h, Fragment } from "preact";

import ButtonGroup from "../ButtonGroup";
import { UserProfession } from "../../foundation/api/model/users/UserProfile";
import { isMobileViewport } from "../../foundation/utils/ua-utils";
import IconArrowDown from "../Icons/IconArrowDown";
import Dropdown from "../Dropdown";
import classname from "../../foundation/utils/classname";
import IconFigmaColor from "../Icons/IconFigmaColor";
import IconSketch from "../Icons/IconSketch";
import IconXD from "../Icons/IconXD";
import IconPhotoshop from "../Icons/IconPhotoshop";

import styles from "./UserProfessionInput.module.css";

const professionCardItemsUntilDesigner = [
    { value: UserProfession.Developer, title: "I’m a developer", description: "Coding is my muse. I came here to build apps that users love to use." },
    { value: UserProfession.Designer, title: "I’m a designer", description: "I'm here to publish and review finalized designs." }
];

const professionCardItemsAfterDesigner = [
    { value: UserProfession.ProductManager, title: "I’m a PM", description: "I'm here to review designs and collaborate with my product team." }
];

const designToolCardItems = [
    { value: "figma", content: <IconFigmaColor/> },
    { value: "sketch", content: <IconSketch/> },
    { value: "adobeXD", content: <IconXD/> },
    { value: "adobePhotoshop", content: <IconPhotoshop/> }
];

const otherProfessions = [
    { value: UserProfession.QualityAssurance, label: "QA" },
    { value: UserProfession.Copywriter, label: "Copywriter" },
    { value: UserProfession.Marketing, label: "Marketing" },
    { value: UserProfession.Other, label: "Other" }
];

interface ProfessionCardProps {
    title: string;
    description: string;
}

interface DesignToolCardProps {
    children: ComponentChildren;
}

function ProfessionCard({ title, description }: ProfessionCardProps) {
    return (
        <div class={styles.professionCard}>
            <h3 class={styles.professionCardTitle}>{title}</h3>
            <p class={styles.professionCardDescription}>{description}</p>
        </div>
    );
}

function DesignToolCard({ children }: DesignToolCardProps) {
    return (
        <div class={styles.designToolCard}>
            {children}
        </div>
    );
}

interface Props {
    profession?: UserProfession;
    designTool?: string;
    otherProfession?: string;
    onProfessionChange: (profession: UserProfession) => void;
    onOtherProfessionChange: (profession: UserProfession) => void;
    onDesignToolChange: (designTool: string) => void;
}

export default function UserProfessionInput({
    profession,
    designTool,
    otherProfession,
    onProfessionChange,
    onOtherProfessionChange,
    onDesignToolChange
}: Props) {
    const isMobileViewPort = isMobileViewport();
    const professionCardItems = isMobileViewPort
        ? professionCardItemsUntilDesigner
        : [...professionCardItemsUntilDesigner, ...professionCardItemsAfterDesigner];

    function handleProfessionSelect(selectedProfession: string) {
        onProfessionChange(selectedProfession as UserProfession);
    }

    function handleOtherProfessionSelect(event: Event) {
        const selectedOtherProfession = (event.currentTarget as HTMLSelectElement).value as UserProfession;
        onOtherProfessionChange(selectedOtherProfession);
    }

    return (
        <>
            <ButtonGroup
                class={styles.professionFormCardGroup}
                alignment={isMobileViewPort ? "vertical" : "horizontal"}
                items={professionCardItems.map(item => ({
                    value: item.value,
                    content: <ProfessionCard title={item.title} description={item.description}/>
                }))}
                selected={profession}
                onChange={handleProfessionSelect}/>
            {profession === UserProfession.Designer && (
                <div className={styles.designToolWrapper}>
                    <p class={styles.designToolDescription}>
                        What's your primary design tool?
                        You'll always be able to publish from all these tools.
                    </p>
                    <ButtonGroup
                        class={styles.designToolCardGroup}
                        items={designToolCardItems.map(item => ({
                            value: item.value,
                            content: <DesignToolCard>{item.content}</DesignToolCard>
                        }))}
                        selected={designTool}
                        onChange={onDesignToolChange}/>
                </div>
            )}
            {isMobileViewPort ? (
                <>
                    <ButtonGroup
                        class={styles.professionFormCardGroupAfterDesigner}
                        alignment={isMobileViewport() ? "vertical" : "horizontal"}
                        items={professionCardItemsAfterDesigner.map(item => ({
                            value: item.value,
                            content: <ProfessionCard title={item.title} description={item.description}/>
                        }))}
                        selected={profession}
                        onChange={handleProfessionSelect}/>
                    <div className={styles.otherProfessionNativeDropdownWrapper}>
                        <select
                            className={styles.otherProfessionNativeDropdown}
                            onChange={handleOtherProfessionSelect}>
                            <option
                                value=""
                                disabled
                                selected={!otherProfession}
                                hidden>
                                I have other profession
                            </option>
                            {otherProfessions.map(item => (
                                <option
                                    key={item.value}
                                    value={item.value}
                                    selected={otherProfession === item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                        <IconArrowDown class={styles.otherProfessionNativeDropdownChevron} width={9}/>
                    </div>
                </>
            ) : (
                <div className={styles.otherProfession}>
                    <span class={styles.otherProfessionLabel}>Other</span>
                    <Dropdown
                        class={classname(styles.otherProfessionDropdown, { [styles.selected]: !!otherProfession })}
                        defaultLabel="Select profession"
                        options={{
                            class: styles.otherProfessionDropdownOptions
                        }}
                        onChange={onOtherProfessionChange}>
                        {otherProfessions.map(item => (
                            <Dropdown.Item
                                selected={otherProfession === item.value}
                                key={item.value}
                                label={item.label}
                                value={item.value}/>
                        ))}
                    </Dropdown>
                </div>
            )}
        </>
    );
}
